'use client';

import type { AutocompleteProps } from '@mui/material';
import { Autocomplete, Divider } from '@mui/material';
import { Fragment, useMemo } from 'react';

export const SELECT_CREATE_NEW = '__create_new__';

type AutocompleteWithNewProps<T> = Omit<
  AutocompleteProps<T | string, false, false, false>,
  'options' | 'getOptionLabel' | 'renderOption'
> & {
  options: T[];
  getOptionLabel?: (option: T | string) => string;
  createNewLabel?: string;
};

export const AutocompleteWithNew = <T,>({
  options,
  getOptionLabel,
  createNewLabel = 'Create new',
  ...props
}: AutocompleteWithNewProps<T>) => {
  const allOptions = useMemo(
    (): (T | string)[] => [SELECT_CREATE_NEW, ...options],
    [options]
  );

  const resolveLabel = (option: T | string): string => {
    if (option === SELECT_CREATE_NEW) return createNewLabel;
    return getOptionLabel ? getOptionLabel(option) : String(option);
  };

  return (
    <Autocomplete
      {...props}
      options={allOptions}
      getOptionLabel={resolveLabel}
      renderOption={(renderProps, option) => {
        const { key, ...optionProps } = renderProps;
        return (
          <Fragment key={key}>
            <li {...optionProps}>{resolveLabel(option)}</li>
            {option === SELECT_CREATE_NEW && !!options.length && (
              <Divider sx={{ my: '8px' }} />
            )}
          </Fragment>
        );
      }}
    />
  );
};
